// ============================================================
// taskActions.ts
// The task quick-action engine + permission rules, SHARED by My Work
// (TasksView) and the client Tasks tab (ClientTasksTab) so a task ticked /
// reprioritised / reassigned on either surface behaves identically and lands
// in BOTH lists:
//   • quickUpdateTask — extracted verbatim from TasksView.handleQuickUpdate:
//     optimistic row patch of store.tasks (My Work's list) + any extra local
//     list (the client tab's), then the SAME updateTaskApi PATCH as the task
//     modal, serialised per task, latest-op-wins, server-truth revert on error.
//     The chain/sequence maps are module-level so the two surfaces share them.
//   • taskPermsFor   — the UI gates TasksView applies (owner / creator-on-task
//     / editColleaguesTasks / admin), never more permissive than the backend.
// ============================================================
import type { Task, UpdateTaskPayload } from '../../types/tasks.types'
import { getTaskByIdApi, updateTaskApi } from '../../api/casevoApi'
import { useCasevoStore, type AuthUser } from '../../store/useCasevoStore'
import { setTaskBaseUpdatedAt } from '../../hooks/saveBaseVersion'
import { isOnTask } from './myWorkLogic'

export type TaskPatcher = (id: string, fn: (t: Task) => Task) => void

const quickChain: Record<string, Promise<unknown>> = {}
const quickSeq: Record<string, number> = {}

// Mirror what the server will store for a quick-action payload.
const applyPayload = (t: Task, payload: UpdateTaskPayload): Task => {
  const next: Task = { ...t }
  if (payload.status !== undefined) {
    next.status = payload.status
    if (payload.status === 'done' && t.status !== 'done') next.completedAt = new Date().toISOString()
    if (payload.status !== 'done') next.completedAt = null
  }
  if (payload.priority !== undefined) next.priority = payload.priority
  if (payload.assignedToUserId !== undefined) next.assignedToUserId = payload.assignedToUserId
  if (payload.firmWide !== undefined) next.firmWide = payload.firmWide
  if (payload.assigneeUserIds !== undefined) next.assignees = payload.assigneeUserIds.map((userId) => ({ userId }))
  return next
}

export const quickUpdateTask = async (id: string, payload: UpdateTaskPayload, alsoPatch?: TaskPatcher): Promise<Task> => {
  const patchRow = (fn: (t: Task) => Task): void => {
    const list = useCasevoStore.getState().tasks
    if (list && list.some((t) => t.id === id)) useCasevoStore.setState({ tasks: list.map((t) => (t.id === id ? fn(t) : t)) })
    alsoPatch?.(id, fn)
  }
  // 1) Optimistic patch.
  patchRow((t) => applyPayload(t, payload))
  // 2) Background save, serialised per task.
  const seq = (quickSeq[id] ?? 0) + 1
  quickSeq[id] = seq
  const prev = quickChain[id] ?? Promise.resolve()
  const op = prev.catch(() => {}).then(() => updateTaskApi(id, payload))
  quickChain[id] = op
  try {
    const updated = await op
    setTaskBaseUpdatedAt(id, updated.updatedAt)
    if (quickSeq[id] === seq) patchRow(() => updated) // latest op wins
    return updated
  } catch (err) {
    // 3) Revert to the server's truth (the task may also be gone / forbidden).
    void getTaskByIdApi(id).then((fresh) => {
      if (quickSeq[id] === seq) patchRow(() => fresh)
    }).catch(() => {})
    throw err
  }
}

export interface TaskPerms {
  currentUserId: string
  isAdmin: boolean
  isOnTaskAsPerson: (t: Task) => boolean
  isOwnTask: (t: Task) => boolean
  canEditColleaguesTasks: boolean
  /** Full edit (priority, reassign, due …) — TasksView canEditTaskFully. */
  canEditFully: (t: Task) => boolean
  /** Tick / status — My Work row canStatus (MyWork.tsx canStatus). */
  canStatus: (t: Task) => boolean
  /** Delete is OWNER-only on the backend (deleteTask) — or admin. */
  canDelete: (t: Task) => boolean
  canAssignPeople: boolean
  canAssignFirm: boolean
}

export const taskPermsFor = (authUser: AuthUser | null | undefined): TaskPerms => {
  const me = authUser?.id ?? ''
  const isAdmin = authUser?.role === 'admin'
  const ep = authUser?.effectivePermissions
  const isOnTaskAsPerson = (t: Task): boolean =>
    t.assignedToUserId === me || (t.assignees ?? []).some((a) => a.userId === me)
  const isOwnTask = (t: Task): boolean =>
    !!me && (t.assignedToUserId === me || (t.createdByUserId === me && isOnTaskAsPerson(t)))
  const canEditColleaguesTasks = !isAdmin && (ep?.seeFirmWideTasks ?? false) && (ep?.editColleaguesTasks ?? false)
  const canEditFully = (t: Task): boolean => isAdmin || isOwnTask(t) || canEditColleaguesTasks
  return {
    currentUserId: me,
    isAdmin,
    isOnTaskAsPerson,
    isOwnTask,
    canEditColleaguesTasks,
    canEditFully,
    canStatus: (t) => isAdmin || isOnTask(t, me) || !!t.firmWide || canEditFully(t),
    canDelete: (t) => isAdmin || t.assignedToUserId === me,
    canAssignPeople: ep?.assignTasksToOthers ?? false,
    canAssignFirm: ep?.createFirmWideTasks ?? false,
  }
}
